import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useClerk,
  useOrganization,
  useOrganizationList,
  useSession,
  useUser,
} from '@clerk/react';
import { useLocation, useNavigate } from '@tanstack/react-router';

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { iconSpinnerThird } from '@/lib/icons';
import {
  acknowledgeOrganizationRemoved,
  wasActiveOrganizationRemoved,
} from '@/store/auth-helper';
import { BRAND } from '@/lib/brand';

interface OnboardingCheckProps {
  children: React.ReactNode;
}

export const OnboardingCheck = ({ children }: OnboardingCheckProps) => {
  const navigate = useNavigate();
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const { user, isLoaded: isUserLoaded } = useUser();
  const { session } = useSession();
  const clerk = useClerk();
  const {
    isLoaded: isOrgListLoaded,
    userMemberships,
    setActive,
    createOrganization,
  } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const isActivatingRef = useRef(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);

  const hasCompletedOnboarding = !!user?.unsafeMetadata?.completed_onboarding;
  const membershipCount = userMemberships?.data?.length ?? 0;
  // User finished onboarding, has no active org, and their membership
  // list is empty - e.g. they were the only member of their workspace
  // and then left. Without this branch the app renders ``null`` and
  // the user sees a blank screen with no recovery path.
  const needsOrgRecovery =
    isOrgLoaded &&
    isOrgListLoaded &&
    isUserLoaded &&
    !organization &&
    hasCompletedOnboarding &&
    membershipCount === 0;

  // If user already completed onboarding but no active org, reactivate their first membership
  useEffect(() => {
    if (
      isOrgLoaded &&
      isOrgListLoaded &&
      isUserLoaded &&
      !organization &&
      hasCompletedOnboarding &&
      membershipCount > 0 &&
      !isActivatingRef.current
    ) {
      // The org the session last had active, when the user is still in it
      // (a user in several orgs expects to land back where they were);
      // otherwise the first membership.
      const lastActiveId = session?.lastActiveOrganizationId;
      const memberships = userMemberships?.data ?? [];
      const lastActive = memberships.find(
        (m) => m.organization.id === lastActiveId
      );
      if (lastActiveId && !lastActive && userMemberships?.hasNextPage) {
        // Clerk pages memberships (10 at a time): look further before
        // falling back to the first one.
        if (!userMemberships.isFetching) void userMemberships.fetchNext?.();
        return;
      }
      const firstOrg = (lastActive ?? memberships[0])?.organization;
      if (firstOrg?.id && setActive) {
        isActivatingRef.current = true;
        setActive({ organization: firstOrg.id })
          .then(() => {
            if (!wasActiveOrganizationRemoved()) return;
            acknowledgeOrganizationRemoved();
            toast.info(
              `That workspace is no longer available. Switched to ${firstOrg.name}.`
            );
          })
          .finally(() => {
            isActivatingRef.current = false;
          });
      }
    }
  }, [
    isOrgLoaded,
    isOrgListLoaded,
    isUserLoaded,
    organization,
    hasCompletedOnboarding,
    membershipCount,
    userMemberships,
    setActive,
    session,
  ]);

  // Redirect to onboarding for users who haven't completed it yet. The
  // router already heading there is enough: the root and the old route each
  // mount a check while the move is pending, and every extra navigate
  // re-rendered the other until React stopped it (Maximum update depth,
  // CLEANLIST-PORTAL-9D).
  const pathname = useLocation({ select: (location) => location.pathname });
  useEffect(() => {
    if (pathname.startsWith('/onboarding')) return;
    if (isOrgLoaded && isUserLoaded && !hasCompletedOnboarding) {
      navigate({ to: '/onboarding', replace: true });
    }
  }, [isOrgLoaded, isUserLoaded, hasCompletedOnboarding, navigate, pathname]);

  if (!isOrgLoaded || !isUserLoaded) {
    // The Lucide Loader2 originally rendered with `animate-spin`. The
    // icon-sweep swap dropped the class, leaving the spinner visibly
    // frozen during the initial Clerk org/user load. CodeRabbit P1 on
    // PR #371.
    return <AppIcon icon={iconSpinnerThird} className='animate-spin' />;
  }

  // Set by the API when an org this user belonged to was suspended. The API
  // suspends any new org they create, so don't offer one.
  const isRestricted = user?.publicMetadata?.org_removed_reason === 'suspended';

  if (needsOrgRecovery && isRestricted) {
    return (
      <div className='fixed inset-0 z-100 flex items-center justify-center overflow-y-auto bg-background px-4 py-10'>
        <div className='flex w-full max-w-sm flex-col gap-5 rounded-xl border border-border bg-card p-6 shadow-xs'>
          <div className='flex flex-col gap-1'>
            <h1 className='text-lg font-semibold text-foreground'>
              Account restricted
            </h1>
            <p className='text-sm text-muted-foreground'>
              Access is paused while we review activity. Contact support if you
              think this is a mistake.
            </p>
          </div>
          <Button asChild variant='brand' className='w-full'>
            <a href='mailto:support@cleanlist.ai'>Contact support</a>
          </Button>
          <Button
            type='button'
            variant='ghost'
            className='-mt-2 w-full'
            onClick={() => void clerk.signOut()}
          >
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  if (needsOrgRecovery) {
    const handleCreateOrg = async (e: React.FormEvent) => {
      e.preventDefault();
      const name = newOrgName.trim();
      if (!name || !createOrganization || isCreatingOrg) return;
      setIsCreatingOrg(true);
      try {
        const created = await createOrganization({ name });
        if (created?.id && setActive) {
          await setActive({ organization: created.id });
        }
        acknowledgeOrganizationRemoved();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to create workspace';
        toast.error(message);
      } finally {
        setIsCreatingOrg(false);
      }
    };

    // Overlay on top of the AppShell (rendered from __root.tsx) -
    // the sidebar stays mounted upstream, so a plain in-flow element
    // ends up cropped behind it. Fixed full-screen + z-index puts
    // this above the shell.
    return (
      <div className='fixed inset-0 z-100 flex items-center justify-center overflow-y-auto bg-background px-4 py-10'>
        <form
          onSubmit={handleCreateOrg}
          className='flex w-full max-w-sm flex-col gap-5 rounded-xl border border-border bg-card p-6 shadow-xs'
        >
          <div className='flex flex-col gap-1'>
            {/* Read at render: the drop happens before Clerk re-renders. */}
            {wasActiveOrganizationRemoved() ? (
              <>
                <h1 className='text-lg font-semibold text-foreground'>
                  This workspace is no longer available
                </h1>
                <p className='text-sm text-muted-foreground'>
                  It was deleted, or you were removed from it. Create a new
                  workspace to keep using {BRAND.name}, or sign out.
                </p>
              </>
            ) : (
              <>
                <h1 className='text-lg font-semibold text-foreground'>
                  Create a workspace to continue
                </h1>
                <p className='text-sm text-muted-foreground'>
                  You're not a member of any workspace yet. Name your new one to
                  keep using {BRAND.name}.
                </p>
              </>
            )}
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='org-name'>Workspace name</Label>
            <Input
              id='org-name'
              autoFocus
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder='Acme Inc.'
              disabled={isCreatingOrg}
            />
          </div>
          <Button
            type='submit'
            disabled={!newOrgName.trim() || isCreatingOrg}
            className='w-full'
          >
            {isCreatingOrg ? (
              <>
                <AppIcon
                  icon={iconSpinnerThird}
                  className='mr-2 animate-spin'
                  style={{ fontSize: 16 }}
                />
                Creating…
              </>
            ) : (
              'Create workspace'
            )}
          </Button>
          <Button
            type='button'
            variant='ghost'
            className='-mt-2 w-full'
            disabled={isCreatingOrg}
            onClick={() => void clerk.signOut()}
          >
            Sign out
          </Button>
        </form>
      </div>
    );
  }

  // Only render children once onboarding is complete and org exists
  return organization && hasCompletedOnboarding ? <>{children}</> : null;
};
